<script setup>
import { ref, watch, computed } from "vue";
import { onClickOutside } from "@vueuse/core";
import { PhMagnifyingGlass, PhX } from "@phosphor-icons/vue"; 

// Props
const props = defineProps({
  options: { type: Array, required: true },
  modelValue: { type: Array, default: () => [] },
  placeholder: String,
  label: String,
  class: String,
  error: {
    type: [String, Array],
    default: null
  }
});

// Emits
const emit = defineEmits(["update:modelValue"]);

const isOpen = ref(false);
const search = ref("");
const selected = ref(Array.isArray(props.modelValue) ? [...props.modelValue] : []);
const wrapper = ref(null);
const serverError = ref(props.error);
const erroredValue = ref(JSON.stringify(props.modelValue));
const lastErrorMessage = ref(null);

// Toggle dropdown
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
}

// Clear all selections
const clearAll = (event) => {
  event.stopPropagation(); // Prevent dropdown from opening
  selected.value = [];
  const newValue = [];
  emit("update:modelValue", newValue);
  checkErrorState(newValue);
};

// Watch for new errors from server
watch(() => props.error, (newError, oldError) => {
  // Get normalized error messages for comparison
  const normalizeError = (err) => {
    if (!err) return null
    return Array.isArray(err) ? err[0] : err
  }
  
  const newErrorMsg = normalizeError(newError)
  const oldErrorMsg = normalizeError(oldError)
  
  if (newErrorMsg) {
    // Check if this is a DIFFERENT error message than before
    const isErrorChanged = newErrorMsg !== oldErrorMsg
    
    // Always show error if:
    // 1. Error message changed (different validation issue)
    // 2. This is the first error
    if (isErrorChanged || !lastErrorMessage.value) {
      serverError.value = newError
      erroredValue.value = JSON.stringify(props.modelValue)
      lastErrorMessage.value = newErrorMsg
    }
  } else {
    // No error from server - clear everything
    serverError.value = null
    lastErrorMessage.value = null
  }
}, { immediate: true });

// Watch modelValue changes
watch(() => props.modelValue, (val) => { 
  selected.value = Array.isArray(val) ? [...val] : [];
});

// Filtered options based on search
const filteredOptions = computed(() => {
  const query = search.value.toLowerCase();
  if (!query) return props.options;
  return props.options.filter(opt =>
    opt.label.toLowerCase().includes(query) ||
    (opt.categories?.some(cat => cat.toLowerCase().includes(query)) ?? false)
  );
});

// Helper to check and update error state
const checkErrorState = (newValue) => {
  const currentValue = JSON.stringify(newValue);
  
  // Clear error if value changes from the errored value
  // Restore error if user returns to the exact errored value
  if (currentValue === erroredValue.value) {
    serverError.value = lastErrorMessage.value;
  } else {
    serverError.value = null;
  }
};

// Toggle single option
const toggleItem = (opt) => {
  const id = opt.value;
  const exists = selected.value.includes(id);
  selected.value = exists
    ? selected.value.filter(v => v !== id)
    : [...selected.value, id];

  const newValue = [...selected.value];
  emit("update:modelValue", newValue);
  checkErrorState(newValue);
};

// Select all / deselect all visible
const toggleSelectAll = () => {
  const allVisible = filteredOptions.value.map(opt => opt.value);
  const allSelected = allVisible.every(id => selected.value.includes(id));

  selected.value = allSelected
    ? selected.value.filter(id => !allVisible.includes(id))
    : Array.from(new Set([...selected.value, ...allVisible]));

  const newValue = [...selected.value];
  emit("update:modelValue", newValue);
  checkErrorState(newValue);
};

// Remove single item
const removeItem = (id) => {
  selected.value = selected.value.filter(v => v !== id);
  const newValue = [...selected.value];
  emit("update:modelValue", newValue);
  checkErrorState(newValue);
};

// Check if selected
const isSelected = (id) => selected.value.includes(id);

// Display error
const displayError = computed(() => {
  if (!serverError.value) return null;
  return Array.isArray(serverError.value) ? serverError.value[0] : serverError.value;
});

// Show clear button only if there are selected items
const showClearButton = computed(() => selected.value.length > 0);

// Click outside to close
onClickOutside(wrapper, () => (isOpen.value = false));
</script>

<template>
  <div ref="wrapper" class="relative w-full" :class="props.class">
    <!-- Label -->
    <label v-if="props.label" class="block mb-1 text-sm font-semibold text-gray-600">
      {{ props.label }}:
    </label>

    <!-- Selected items -->
    <div
      @click="toggleDropdown"
      class="flex min-h-9 cursor-pointer items-center gap-1 rounded-md bg-white px-2 py-1 text-sm transition-all"
      :class="[
        'border',
        displayError
          ? 'border-red-500 focus:ring-red-300'
          : 'border-gray-300 hover:outline hover:outline-primary-400'
      ]"
    >
      <!-- Left side: placeholder or selected items -->
      <div class="flex flex-wrap items-center gap-1 flex-1 min-w-0">
        <span v-if="selected.length === 0" class="text-gray-400">
          {{ props.placeholder ?? "Select..." }}
        </span>

        <template v-else>
          <span
            v-for="id in selected"
            :key="id"
            class="group flex items-center gap-1 rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
          >
            {{ props.options.find(o => o.value === id)?.label ?? id }}
            <button type="button" @click.stop="removeItem(id)" class="text-gray-500 hover:text-gray-700">✕</button>
          </span>
        </template>
      </div>

      <!-- Right side: Clear button + Dropdown arrow (always visible) -->
      <div class="flex items-center gap-1.5 shrink-0">
        <!-- Clear button (appears before dropdown arrow) -->
        <button
          v-if="showClearButton"
          type="button"
          @click.stop="clearAll"
          class="text-gray-500 hover:text-red-600 transition-colors p-0.5 rounded hover:bg-gray-100"
          title="Clear all"
        >
          <PhX :size="18" weight="bold" />
        </button>

        <!-- Dropdown arrow -->
        <svg
          class="h-4 w-4 text-gray-500 transition-transform duration-200"
          :class="{ 'rotate-180': isOpen }"
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <!-- Dropdown -->
    <div v-if="isOpen" class="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
      <!-- Search input with icon -->
      <div v-if="filteredOptions.length > 0" class="relative px-2 py-2">
        <PhMagnifyingGlass class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" :size="16" />
        <input
          v-model="search"
          type="text"
          class="w-full rounded-md border border-gray-300 bg-white pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-400"
          placeholder="Search..."
        />
      </div>

      <!-- Select All / Deselect All -->
      <div
        v-if="filteredOptions.length"
        class="cursor-pointer px-3 py-1.5 text-sm hover:bg-gray-50 border-b border-gray-100 flex items-center justify-between"
        @click="toggleSelectAll"
      >
        <span>Select All</span>
        <svg v-if="filteredOptions.every(opt => selected.includes(opt.value))" class="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <!-- Options list -->
      <ul v-if="filteredOptions.length" class="max-h-32 overflow-y-auto divide-y divide-gray-100">
        <li
          v-for="opt in filteredOptions"
          :key="opt.value"
          @click="toggleItem(opt)"
          class="flex cursor-pointer items-center px-3 py-1.5 text-sm hover:bg-gray-50"
          :class="{ 'bg-gray-50': isSelected(opt.value) }"
        >
          <span class="flex-1">{{ opt.label }}</span>
          <svg v-if="isSelected(opt.value)" class="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </li>
      </ul>

      <!-- No results -->
      <div v-else class="p-2 text-sm text-gray-500">No results found</div>
    </div>

    <!-- Error -->
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <span v-if="displayError" class="text-red-600 text-sm mt-1 block">
        {{ displayError }}
      </span>
    </transition>
  </div>
</template>